import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {
	PaymentTransaction,
	PaymentUploadResponse,
	PaymentProcessingService,
} from '@lmc-app/core/services/payment-processing.service';
import { LoanAccountMatchDialogComponent } from './loan-account-match-dialog.component';
import {
	TransactionPreviewStatus,
	PREVIEW_STATUS_LABELS,
	getPreviewStatusColor,
	isHeldBackPreviewStatus,
	isSubmittablePreviewStatus,
} from '@lmc-app/shared/enum/transaction-preview-status.enum';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';

export interface TransactionsPreviewDialogData {
	parsedFile: PaymentUploadResponse['parsedFile'];
	lookupResults: PaymentTransaction[];
}

export type TransactionsPreviewDialogResult = 'submitted' | undefined;

@Component({
	selector: 'app-transactions-preview-dialog',
	templateUrl: './transactions-preview-dialog.component.html',
	styleUrls: ['./transactions-preview-dialog.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		MatCardModule,
		MatExpansionModule,
		MatIconModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		MatSlideToggleModule,
		MatTooltipModule,
	],
})
export class TransactionsPreviewDialogComponent implements OnDestroy {
	isSubmitting = false;

	/** Paid-off rows are never submitted, so their list starts collapsed. */
	isPaidOffExpanded = false;

	/**
	 * Lets an operator submit without correcting the held-back rows. They are still sent,
	 * and the backend persists them as Invalid — kept on the batch and never forwarded to
	 * KapCharge until their data is fixed.
	 */
	skipCorrections = false;

	readonly TransactionPreviewStatus = TransactionPreviewStatus;

	lookupResults: PaymentTransaction[];

	/** Submittable rows in display order (attention first); the cards render a page of these. */
	previewRows: PaymentTransaction[] = [];

	pageIndex = 0;
	pageSize = 10;

	private readonly destroy$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<TransactionsPreviewDialogComponent, TransactionsPreviewDialogResult>,
		@Inject(MAT_DIALOG_DATA) public data: TransactionsPreviewDialogData,
		private readonly paymentProcessingService: PaymentProcessingService,
		private readonly dialog: MatDialog,
		private readonly cdRef: ChangeDetectorRef,
	) {
		// The backend lookup already set each row's status and the message behind it. The
		// frontend never derives status from the lookup — only a successful in-place fix
		// changes it (to ManualMatch).
		this.lookupResults = [...data.lookupResults];
		this.buildPreviewRows();
	}

	statusLabel(status?: TransactionPreviewStatus): string {
		return status ? PREVIEW_STATUS_LABELS[status] : '';
	}

	statusColor(status?: TransactionPreviewStatus): string {
		return getPreviewStatusColor(status);
	}

	/** True for rows that must be corrected before the file can be submitted. */
	isBlocking(status?: TransactionPreviewStatus): boolean {
		return isHeldBackPreviewStatus(status);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get parsedFile(): PaymentUploadResponse['parsedFile'] {
		return this.data.parsedFile;
	}

	/**
	 * Rows that must be corrected before the file can be submitted. Submission is
	 * all-or-nothing, so a single one of these disables Submit.
	 */
	get blockingResults(): PaymentTransaction[] {
		return this.submittableResults.filter((tx) => isHeldBackPreviewStatus(tx.status));
	}

	/** Paid-off rows are shown in a separate list and are never submitted. */
	get paidOffResults(): PaymentTransaction[] {
		return this.lookupResults.filter((tx) => !isSubmittablePreviewStatus(tx.status));
	}

	/** Submittable rows: everything except paid-off loans. */
	get submittableResults(): PaymentTransaction[] {
		return this.lookupResults.filter((tx) => isSubmittablePreviewStatus(tx.status));
	}

	/**
	 * Submit is enabled once there is something to send and nothing left to correct — or the
	 * operator has skipped the corrections, in which case the held-back rows go along and are
	 * persisted as Invalid.
	 */
	get canSubmit(): boolean {
		if (this.isSubmitting || this.submittableResults.length === 0) return false;
		return this.blockingResults.length === 0 || this.skipCorrections;
	}

	/**
	 * Total debit amount of the submittable rows (not the parsed file), so any manual
	 * edits an operator makes to a transaction's amount are reflected here.
	 */
	get totalDebitAmount(): number {
		return this.submittableResults.reduce((sum, tx) => sum + tx.amount, 0);
	}

	formatAmount(amountInCents: number): string {
		return FormatUtil.formatCurrency(amountInCents / 100);
	}

	/** The page of cards currently on screen. */
	get pagedRows(): PaymentTransaction[] {
		const start = this.pageIndex * this.pageSize;
		return this.previewRows.slice(start, start + this.pageSize);
	}

	onPageChange(event: PageEvent): void {
		this.pageIndex = event.pageIndex;
		this.pageSize = event.pageSize;
	}

	/**
	 * Rows are mutated in place by an in-place fix (onEditTransaction) and re-sorted around
	 * it, so identity — not index — is what keeps a card bound to its transaction.
	 */
	trackByRow(_index: number, row: PaymentTransaction): PaymentTransaction {
		return row;
	}

	/**
	 * Corrects an Invalid row's bank details to re-resolve the loan account. A customer's
	 * bank account cannot be changed in NLS, so this only re-runs the lookup with the
	 * corrected values; a successful match marks the row ManualMatch.
	 */
	onEditTransaction(tx: PaymentTransaction): void {
		const ref = this.dialog.open(LoanAccountMatchDialogComponent, { width: '460px', data: { tx } });
		ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: PaymentTransaction | undefined) => {
			if (!result) return;
			// Only a clean re-lookup counts as a user fix (ManualMatch). Any other outcome —
			// a paid-off loan, or customer data that is still incomplete — keeps the status
			// the backend returned, so the row stays blocking or moves to the paid-off list.
			Object.assign(tx, result);
			if (result.status === TransactionPreviewStatus.Valid) {
				tx.status = TransactionPreviewStatus.ManualMatch;
				tx.error = null;
			}
			this.buildPreviewRows();
			this.cdRef.detectChanges();
		});
	}

	/**
	 * Exports the rows held back for correction to a spreadsheet, so the customer data can
	 * be fixed in NLS away from this dialog. The sheet carries what the file said, what the
	 * lookup found and the note explaining what is wrong — nothing is submitted or changed.
	 */
	onExportCorrections(): void {
		const rows = this.blockingResults;
		if (rows.length === 0) return;

		const sheetRows = rows.map((tx) => ({
			Status: this.statusLabel(tx.status),
			'Loan #': tx.loanNumber ?? '',
			'Recipient (File)': tx.recipientName ?? '',
			'Customer Name (NLS)': tx.customerName ?? '',
			'Institution Code': tx.institutionCode ?? '',
			'Transit Number': tx.transitNumber ?? '',
			'Account Number': tx.accountNumber ?? '',
			'Branch Name': tx.branchName ?? '',
			Amount: tx.amount / 100,
			'Due Date': tx.dueDate ?? '',
			'Correction Required': tx.error ?? '',
		}));

		const worksheet = XLSX.utils.json_to_sheet(sheetRows);
		worksheet['!cols'] = [
			{ wch: 20 }, // Status
			{ wch: 14 }, // Loan #
			{ wch: 26 }, // Recipient (File)
			{ wch: 26 }, // Customer Name (NLS)
			{ wch: 16 }, // Institution Code
			{ wch: 15 }, // Transit Number
			{ wch: 18 }, // Account Number
			{ wch: 26 }, // Branch Name
			{ wch: 12 }, // Amount
			{ wch: 12 }, // Due Date
			{ wch: 50 }, // Correction Required
		];
		this.formatAmountColumn(worksheet, sheetRows.length);

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Corrections Required');

		const sourceName = (this.parsedFile.header.fileName ?? 'payment-file').replace(/\.[^.]+$/, '');
		XLSX.writeFile(workbook, `Corrections_${sourceName}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
	}

	onSubmit(): void {
		if (!this.canSubmit) return;

		this.isSubmitting = true;

		const { customerNumber: originatorId, fileType, fileName } = this.parsedFile.header;
		// Paid-off rows are excluded; every other row goes as it stands and the backend maps
		// its preview status — Valid / ManualMatch to Queued, anything still needing
		// correction (only possible with skipCorrections on) to Invalid.
		this.paymentProcessingService
			.submitTransactions(this.submittableResults, originatorId, fileType, fileName)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.isSubmitting = false;
					this.dialogRef.close('submitted');
				},
				error: (err) => {
					this.isSubmitting = false;
					this.dialog.open(LmcDialogComponent, {
						data: {
							title: 'Submission Failed',
							content: err?.error?.message ?? err?.message ?? 'Submission failed. Please try again.',
							positiveButtonText: 'OK',
						},
					});
				},
			});
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	/** Shows the Amount column (column I) as currency-style numbers rather than raw values. */
	private formatAmountColumn(worksheet: XLSX.WorkSheet, rowCount: number): void {
		for (let row = 2; row <= rowCount + 1; row++) {
			const cell = worksheet[`I${row}`];
			if (cell) cell.z = '#,##0.00';
		}
	}

	private buildPreviewRows(): void {
		// Only submittable rows go in the main list (paid-off rows are listed separately).
		// Float rows needing attention to the top, then fixed, then valid.
		const rows = this.submittableResults;
		const blocking = rows.filter((tx) => isHeldBackPreviewStatus(tx.status));
		const manualMatch = rows.filter((tx) => tx.status === TransactionPreviewStatus.ManualMatch);
		const valid = rows.filter((tx) => tx.status === TransactionPreviewStatus.Valid);
		this.previewRows = [...blocking, ...manualMatch, ...valid];

		// A fix re-sorts the list, and correcting the last rows can shorten it. Clamp rather
		// than reset, so an operator working through page 4 stays on page 4.
		const lastPage = Math.max(0, Math.ceil(this.previewRows.length / this.pageSize) - 1);
		if (this.pageIndex > lastPage) this.pageIndex = lastPage;
	}
}